import { useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { X, Plus, CloudUpload, FileText, Check } from 'lucide-react'
import { Button } from './Button'
import { Dropdown } from './Dropdown'
import { showToastMessage } from './Toast'
import { getScheduledWeekOptions, getDefaultScheduledWeek } from '../features/academics/create-lesson-plan/utils/planWeeksUtils'

export interface TopicConfig {
  id: string
  name: string
  scheduledWeek: string
  pedagogicalNotes: string
  attachments: Array<{ fileName: string; size: string; subtitle?: string; file?: File; stagingKey?: string }>
  isSaved?: boolean
}

export interface AddChapterModalProps {
  open: boolean
  onClose: () => void
  onAddChapter: (chapter: { name: string; topics: any[] }) => Promise<void> | void
  gradeName?: string
  subjectName?: string
  existingChapters?: Array<{ name: string; topics?: Array<{ scheduledWeek?: string | number | null }> }>
  totalWeeks?: number
  sectionCount?: number
  isSubmitting?: boolean
}


export function InlineTopicConfigForm({
  chapNum,
  topicNum,
  topic,
  totalWeeks = 16,
  onSaveTopic,
  onCancelTopic,
  disabled = false,
}: {
  chapNum: number
  topicNum: number
  topic: TopicConfig
  totalWeeks?: number
  onSaveTopic: (updatedTopic: TopicConfig) => void
  onCancelTopic: () => void
  disabled?: boolean
}) {
  const [topicName, setTopicName] = useState(topic.name || '')
  const [scheduledWeek, setScheduledWeek] = useState(
    topic.scheduledWeek || getDefaultScheduledWeek([], 0, totalWeeks, false)
  )
  const [notes, setNotes] = useState(topic.pedagogicalNotes || '')
  const [attachments, setAttachments] = useState(topic.attachments || [])
  const [isSaved, setIsSaved] = useState(Boolean(topic.isSaved))

  const weekOptions = useMemo(
    () => getScheduledWeekOptions(totalWeeks, scheduledWeek, false),
    [totalWeeks, scheduledWeek]
  )

  const handleSave = () => {
    if (!topicName.trim()) return
    const updated: TopicConfig = {
      ...topic,
      name: topicName,
      scheduledWeek,
      pedagogicalNotes: notes,
      attachments,
      isSaved: true,
    }
    setIsSaved(true)
    onSaveTopic(updated)
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    const newAtts = Array.from(files).map((f) => ({
      fileName: f.name,
      size: `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
      subtitle: f.type || 'Resource File',
      file: f,
    }))
    setAttachments((prev) => [...prev, ...newAtts])
    showToastMessage(`${files.length} resource(s) added`, 'success')
    setIsSaved(false)
  }

  const handleRemoveAttachment = (idx: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== idx))
    setIsSaved(false)
  }

  return (
    <div className="rounded-xl border border-neutral-200 bg-neutral-50/60 p-5 space-y-4 font-sans">
      {/* Topic Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-[#0a5b63]" />
          <span className="text-xs font-bold text-neutral-900 font-sans">
            Topic {chapNum}.{topicNum} Configuration
          </span>
        </div>
        {isSaved && (
          <span className="text-[11px] text-neutral-400 font-sans">
            Saved
          </span>
        )}
      </div>

      {/* Row 1: Topic Title & Scheduled Week */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-8 space-y-1.5">
          <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
            Topic Title *
          </label>
          <input
            type="text"
            disabled={disabled}
            value={topicName}
            onChange={(e) => {
              setTopicName(e.target.value)
              setIsSaved(false)
            }}
            placeholder="e.g. Flower Anatomy & Pollination Mechanisms"
            className="w-full h-10 rounded-lg border border-neutral-200 bg-white px-3 text-xs font-medium text-neutral-900 focus:border-[#0a5b63] focus:outline-none font-sans disabled:opacity-50"
          />
        </div>

        <div className="md:col-span-4 space-y-1.5">
          <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
            Scheduled Week
          </label>
          <Dropdown
            disabled={disabled}
            options={weekOptions}
            value={scheduledWeek}
            onChange={(val) => {
              setScheduledWeek(val)
              setIsSaved(false)
            }}
            placeholder="Select Week"
            className="w-full"
          />
        </div>
      </div>

      {/* Row 2: Pedagogical Notes & Lab Directives */}
      <div className="space-y-1.5">
        <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
          Pedagogical Notes & Content
        </label>
        <textarea
          rows={3}
          disabled={disabled}
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value)
            setIsSaved(false)
          }}
          placeholder="Detailed topic overview, key objectives, lab notes or instructional instructions..."
          className="w-full rounded-lg border border-neutral-200 bg-white p-3 text-xs text-neutral-800 focus:border-[#0a5b63] focus:outline-none font-sans resize-none leading-relaxed disabled:opacity-50"
        />
      </div>

      {/* Row 3: Attached Curricular Resources */}
      <div className="space-y-2">
        <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
          Attached Resources
        </label>

        <div className="flex flex-wrap items-center gap-3">
          {attachments.map((att, idx) => {
            const displayName = att.fileName || att.file?.name || 'Attachment'
            return (
              <div
                key={idx}
                title={displayName}
                className="flex items-center gap-2 rounded-lg border border-neutral-200 bg-white p-2.5 shadow-2xs text-xs font-sans max-w-xs"
              >
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-[#e6f4f1] text-[#0a5b63]">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-neutral-900 truncate" title={displayName}>
                    {displayName}
                  </p>
                  <p className="text-[11px] text-neutral-400 truncate">
                    {att.size ? att.size : ''} {att.subtitle ? `• ${att.subtitle}` : ''}
                  </p>
                </div>
                {!disabled && (
                  <button
                    type="button"
                    onClick={() => handleRemoveAttachment(idx)}
                    className="text-neutral-400 hover:text-neutral-600 p-1"
                    title="Remove resource"
                    aria-label="Remove resource"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            )
          })}

          {!disabled && (
            <label className="flex-1 min-w-[220px] h-14 rounded-lg border border-dashed border-neutral-300 bg-neutral-50/80 hover:bg-neutral-100/60 hover:border-neutral-400 transition flex items-center justify-center gap-2 text-xs font-bold text-[#0a5b63] font-sans cursor-pointer">
              <CloudUpload className="h-4 w-4" />
              <span>+ Attach File(s)</span>
              <input type="file" multiple className="hidden" onChange={handleFileChange} />
            </label>
          )}
        </div>
      </div>

      {/* Row 4: Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-2">
        <button
          type="button"
          disabled={disabled}
          onClick={onCancelTopic}
          className="text-xs font-bold text-neutral-500 hover:text-neutral-700 font-sans px-3 py-1.5 disabled:opacity-50"
        >
          Remove
        </button>
        <Button
          type="button"
          disabled={disabled || !topicName.trim()}
          onClick={handleSave}
          className="h-8 px-4 text-xs font-bold bg-[#0a5b63] hover:bg-[#08484f] text-white inline-flex items-center gap-1.5 disabled:opacity-60 disabled:bg-[#0a5b63] disabled:text-white"
        >
          <Check className="h-3.5 w-3.5" />
          Save Topic Info
        </Button>
      </div>
    </div>
  )
}

export function AddChapterModal({
  open,
  onClose,
  onAddChapter,
  gradeName = 'Grade 5',
  subjectName = 'Science',
  existingChapters = [],
  totalWeeks = 16,
  sectionCount = 1,
  isSubmitting = false,
}: AddChapterModalProps) {
  const nextChapterNum = existingChapters.length + 1
  const priorChapterTitle =
    existingChapters.length > 0 ? existingChapters[existingChapters.length - 1].name : null

  const positionIndicatorText = priorChapterTitle
    ? `Chapter ${nextChapterNum} (Follows Chapter ${existingChapters.length}: ${priorChapterTitle})`
    : `Chapter 1 (First chapter in this plan)`

  const [chapterTitle, setChapterTitle] = useState('')
  const [topics, setTopics] = useState<TopicConfig[]>([
    {
      id: `topic-${Date.now()}-1`,
      name: '',
      scheduledWeek: getDefaultScheduledWeek(existingChapters, existingChapters.length, totalWeeks, false),
      pedagogicalNotes: '',
      attachments: [],
      isSaved: false,
    },
  ])

  if (!open) return null

  const handleUpdateTopic = (idx: number, updated: TopicConfig) => {
    setTopics((prev) => prev.map((t, i) => (i === idx ? updated : t)))
  }

  const handleRemoveTopic = (idx: number) => {
    if (topics.length <= 1) return
    setTopics((prev) => prev.filter((_, i) => i !== idx))
  }

  const handleAddAnotherTopic = () => {
    setTopics((prev) => {
      const nextWeek = getDefaultScheduledWeek([{ topics: prev }], 0, totalWeeks, false)
      return [
        ...prev,
        {
          id: `topic-${Date.now()}-${prev.length + 1}`,
          name: '',
          scheduledWeek: nextWeek,
          pedagogicalNotes: '',
          attachments: [],
          isSaved: false,
        },
      ]
    })
  }

  const isFormValid =
    chapterTitle.trim().length > 0 &&
    topics.length > 0 &&
    topics.every((t) => t.name.trim().length > 0)

  const handleCreateChapter = async () => {
    if (!isFormValid || isSubmitting) return
    await onAddChapter({
      name: chapterTitle.trim(),
      topics,
    })
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/45 px-3 sm:px-4 backdrop-blur-[6px] py-4 sm:py-6 font-sans overflow-hidden"
      onMouseDown={isSubmitting ? undefined : onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-[760px] max-h-[90vh] rounded-2xl bg-white shadow-2xl overflow-hidden border border-neutral-200 flex flex-col font-sans my-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-neutral-100 p-4 sm:p-6 bg-white shrink-0">
          <div className="flex items-start gap-3.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e6f4f1] text-[#0a5b63]">
              <FileText className="h-5 w-5" />
            </div>
            <div className="space-y-0.5">
              <h2 className="text-base sm:text-lg font-bold text-neutral-900 font-sans">
                Add New Chapter to Syllabus
              </h2>
              <p className="text-xs text-neutral-500 font-sans">
                Define a new curriculum unit, set its instructional timeline, and position it within {gradeName} {subjectName}.
              </p>
            </div>
          </div>

          <button
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition disabled:opacity-40"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-6 space-y-6 flex-1 min-h-0 overflow-y-auto bg-white">
          {sectionCount > 1 && (
            <div className="rounded-xl border border-teal-200 bg-[#e6f4f1]/60 p-3.5 text-xs text-[#0a5b63] font-medium font-sans flex items-center gap-2">
              <span className="font-bold">Note:</span>
              <span>This plan is shared by {sectionCount} sections. The new chapter is added to all of them.</span>
            </div>
          )}

          {/* Section 1: Chapter Identification */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-wider text-neutral-700 font-sans">
                  📋 1. Chapter Identification
                </span>
              </div>
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#e6f4f1] text-[#0a5b63] font-bold text-[11px] font-sans">
                • {positionIndicatorText}
              </span>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <label className="block text-xs font-bold text-neutral-700 uppercase font-sans">
                  Chapter Title *
                </label>
                <span className="text-[11px] text-neutral-400 font-sans">Max 80 chars</span>
              </div>
              <input
                type="text"
                maxLength={80}
                disabled={isSubmitting}
                value={chapterTitle}
                onChange={(e) => setChapterTitle(e.target.value)}
                placeholder="e.g. Earth's Atmosphere & Weather Dynamics"
                className="w-full h-11 rounded-xl border border-neutral-200 bg-white px-4 text-xs font-bold text-neutral-900 focus:border-[#0a5b63] focus:outline-none font-sans disabled:opacity-50"
              />
            </div>
          </div>

          {/* Inline Topic Editors */}
          <div className="space-y-4">
            {topics.map((t, idx) => (
              <InlineTopicConfigForm
                key={t.id}
                chapNum={nextChapterNum}
                topicNum={idx + 1}
                topic={t}
                totalWeeks={totalWeeks}
                disabled={isSubmitting}
                onSaveTopic={(updated) => handleUpdateTopic(idx, updated)}
                onCancelTopic={() => handleRemoveTopic(idx)}
              />
            ))}

            {/* Add Another Topic button */}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={handleAddAnotherTopic}
              className="w-full py-3 rounded-xl border border-dashed border-neutral-200 bg-neutral-50/60 hover:bg-neutral-100/60 text-xs font-bold text-[#0a5b63] transition flex items-center justify-center gap-1.5 font-sans disabled:opacity-40"
            >
              <Plus className="h-4 w-4" />
              Add Another Topic to Chapter {nextChapterNum}
            </button>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-between border-t border-neutral-200 bg-white p-4 shrink-0">
          <Button
            variant="secondary"
            type="button"
            disabled={isSubmitting}
            onClick={onClose}
            className="h-10 px-5 text-xs font-bold border border-neutral-300 text-neutral-700 bg-white hover:bg-neutral-50 disabled:opacity-60 disabled:bg-white disabled:text-neutral-700"
          >
            Cancel
          </Button>

          <Button
            type="button"
            isLoading={isSubmitting}
            disabled={!isFormValid || isSubmitting}
            onClick={handleCreateChapter}
            className="h-10 px-5 text-xs font-bold bg-[#0a5b63] hover:bg-[#08484f] text-white inline-flex items-center gap-1.5 shadow-sm disabled:opacity-60 disabled:bg-[#0a5b63] disabled:text-white"
          >
            {!isSubmitting && <Plus className="h-4 w-4" />}
            {isSubmitting ? 'Creating...' : 'Create Chapter'}
          </Button>
        </div>
      </div>
    </div>
  )


  return typeof document === 'undefined' ? modalContent : createPortal(modalContent, document.body)
}

export default AddChapterModal
